import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Form, Input, Modal, Radio, Select, Spin, notification } from 'antd'
import { useDispatch, useSelector } from 'react-redux'

import { createPosition, getDictList, getExamDictData, updatePosition } from '../common/request'
import ResponseCode from '../common/response-code'
import { positionFieldTypeMap } from '../common/common'
import { updateDictInfo, updateDictList } from '../store/slice/dict-slice'
import { replaceChineseInitials } from '../plugins/utils'

const JOIN_SELECT_FIELD_TYPES = [positionFieldTypeMap.select]

const normalizeFieldType = (fieldType) => {
  if (String(fieldType) === '1') {
    return positionFieldTypeMap.input
  }
  return fieldType
}

/**
 * 职位字段编辑弹窗（系统级 + 机构级共用）
 *
 * 提交参数差异：
 * - 系统级（无 orgItem）：requiredSystem=1, fieldLevel='S'，排序取自表单 fieldSort
 * - 机构级（有 orgItem）：orgId、requiredSystem=0, fieldLevel='U'，fieldSort 固定 999
 */
const EditPositionFieldModal = forwardRef((props, ref) => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const dictList = useSelector(state => state.dict.dictList)
  const dictInfo = useSelector(state => state.dict.dictInfo)
  const isOrgScope = Boolean(props.orgItem?.id)
  const [modalVisible, setModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [dictListOptions, setDictListOptions] = useState([])
  const [fieldTypeOptions, setFieldTypeOptions] = useState([])
  const [notifyApi, contextHolder] = notification.useNotification()
  const [fieldJoinSelectFormVisible, setFieldJoinSelectFormVisible] = useState(false)

  useEffect(() => {
    if (modalVisible) {
      if (props.modalType === 'create') {
        setModalTitle('新增职位字段')
      } else {
        setModalTitle('编辑职位字段')
        const fieldType = normalizeFieldType(props.fieldItem.fieldType)
        form.setFieldsValue({
          ...props.fieldItem,
          fieldType
        })
        updateFieldJoinSelectVisible(fieldType, props.fieldItem.fieldJoinSelect)
      }

      getFieldTypeList()
      getDictListAction()
    }
  }, [modalVisible])

  useImperativeHandle(ref, () => {
    return {
      showModal
    }
  })

  const transformDictData = (data) => {
    return data?.map(item => {
      return {
        label: item.dictLabel,
        value: item.dictValue
      }
    }) || []
  }

  const getFieldTypeList = () => {
    if (dictInfo['sys_zdlx']) {
      setFieldTypeOptions(transformDictData(dictInfo['sys_zdlx']))
    } else {
      getExamDictData({ dictCode: 'sys_zdlx' }).then(res => {
        if (res.code === ResponseCode.success) {
          setFieldTypeOptions(transformDictData(res.data))
          dispatch(updateDictInfo({ dictKey: 'sys_zdlx', dictValue: res.data }))
        } else {
          Modal.error({
            title: '错误',
            centered: true,
            content: res.msg
          })
        }
      }).catch(() => {
        Modal.error({
          title: '网络错误',
          centered: true,
          content: '查询字段类型字典失败'
        })
      })
    }
  }

  const updateFieldJoinSelectVisible = (fieldType, fieldJoinSelect) => {
    const needJoinSelect = JOIN_SELECT_FIELD_TYPES.includes(String(fieldType))
    setFieldJoinSelectFormVisible(needJoinSelect || Boolean(fieldJoinSelect))
  }

  const getDictListAction = () => {
    if (dictList.length > 0) {
      setDictListOptions(dictList)
    } else {
      const params = {
        orderBy: 'id',
        pageNum: 1,
        pageSize: 9999
      }
      getDictList(params).then(res => {
        if (res.code === ResponseCode.success && res.data) {
          setDictListOptions(res.data.elements)
          dispatch(updateDictList({ dictList: res.data.elements }))
        }
      })
    }
  }

  const buildSubmitParams = (fields) => {
    if (isOrgScope) {
      return {
        ...fields,
        orgId: props.orgItem.id,
        requiredSystem: 0,
        fieldLevel: 'U',
        fieldSort: 999,
        ...(props.modalType !== 'create' && { id: props.fieldItem.id })
      }
    }

    return {
      ...fields,
      requiredSystem: 1,
      fieldLevel: 'S',
      ...(props.modalType !== 'create' && { id: props.fieldItem.id })
    }
  }

  const showModal = () => {
    setModalVisible(true)
  }

  const hideModal = () => {
    setModalVisible(false)
    setFieldJoinSelectFormVisible(false)
    form.resetFields()
  }

  const savePositionField = (fields) => {
    const params = buildSubmitParams(fields)
    const request = props.modalType === 'create'
      ? createPosition(params)
      : updatePosition(params)

    setLoading(true)
    request.then(res => {
      setLoading(false)
      if (res.code === ResponseCode.success) {
        notifyApi.success({
          message: '操作成功'
        })
        hideModal()
        props.insertSuccess && props.insertSuccess()
      } else {
        Modal.error({
          title: '错误',
          content: res.msg,
          centered: true
        })
      }
    }).catch(() => {
      setLoading(false)
      Modal.error({
        title: '网络错误',
        content: props.modalType === 'create' ? '创建职位字段失败' : '编辑职位字段失败',
        centered: true
      })
    })
  }

  const onOk = () => {
    form.validateFields().then(fields => {
      savePositionField(fields)
    })
  }

  const getFieldJoinSelectForm = (visible) => {
    const options = dictListOptions.map(item => {
      return {
        label: item.dictName,
        value: item.dictCode
      }
    })

    if (visible) {
      return (
        <Form.Item
          label="关联数据"
          name="fieldJoinSelect"
          rules={ [{ required: true, message: '请选择关联数据项' }] }
        >
          <Select options={ options } placeholder="请选择关联数据项"></Select>
        </Form.Item>
      )
    }

    return <></>
  }

  const fieldTypeChange = (value) => {
    const needJoinSelect = JOIN_SELECT_FIELD_TYPES.includes(String(value))
    setFieldJoinSelectFormVisible(needJoinSelect)
    if (!needJoinSelect) {
      form.setFieldValue('fieldJoinSelect', undefined)
    }
  }

  const fieldNameChange = (e) => {
    if (e.target.value.trim()) {
      form.setFieldValue('fieldCode', `p_${ replaceChineseInitials(e.target.value) }`)
    }
  }

  return (
    <div ref={ ref }>
      { contextHolder }
      <Modal
        centered
        title={ modalTitle }
        open={ modalVisible }
        confirmLoading={ loading }
        onOk={ onOk }
        onCancel={ hideModal }
      >
        <Spin spinning={ loading }>
          <Form
            className="mt-4"
            form={ form }
            labelCol={ {
              span: 8
            } }
            wrapperCol={ {
              span: 14
            } }
            initialValues={ {
              fieldType: positionFieldTypeMap.input,
              fieldIsNotBlank: 1,
              state: 1
            } }
          >
            <Form.Item
              label="职位字段名称"
              name="fieldName"
              rules={ [{ required: true, message: '职位字段名称不能为空' }] }
            >
              <Input placeholder="请输入职位字段名称" onChange={ fieldNameChange }></Input>
            </Form.Item>
            <Form.Item
              label="职位字段编码"
              name="fieldCode"
              rules={ [{ required: true, message: '职位字段编码不能为空' }] }
            >
              <Input disabled placeholder="请输入职位字段名称"></Input>
            </Form.Item>
            <Form.Item
              label="字段类型"
              name="fieldType"
              rules={ [{ required: true, message: '请选择字段类型' }] }
            >
              <Select
                options={ fieldTypeOptions }
                style={ { width: '220px' } }
                placeholder="请选择字段类型"
                onChange={ fieldTypeChange }
              ></Select>
            </Form.Item>
            { getFieldJoinSelectForm(fieldJoinSelectFormVisible) }

            <Form.Item
              label="是否必填"
              name="fieldIsNotBlank"
            >
              <Radio.Group>
                <Radio value={ 1 }>是</Radio>
                <Radio value={ 0 }>否</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              label="是否启用"
              name="state"
            >
              <Radio.Group>
                <Radio value={ 1 }>是</Radio>
                <Radio value={ 0 }>否</Radio>
              </Radio.Group>
            </Form.Item>
            {
              !isOrgScope
                ? (
                  <Form.Item
                    label="排序"
                    name="fieldSort"
                  >
                    <Input></Input>
                  </Form.Item>
                )
                : <></>
            }
          </Form>
        </Spin>
      </Modal>
    </div>
  )
})

export default EditPositionFieldModal
